import { Injectable } from '@angular/core';
import {
  getPermissionValue,
  IAppDashboard,
  IMenuItemModulePermission,
  IUser,
  IUserRole,
  ModelService,
  PermissionLoader,
  SessionService,
} from 'core-client/api';
import { IDashboardGroups, splitDashboards } from './dashboard-split.util';

export interface IDashboardSelectionView {
  groups: IDashboardGroups;
  activeDashboardId: string;
  /** Standard copy-type dashboards that cannot be activated while the user's personal copy is missing. */
  blockedDashboardIds: string[];
  canCreateStandard: boolean;
  canListStandard: boolean;
  canListPersonal: boolean;
}

export type DashboardActivation = 'activated' | 'denied';

function defaultDashboardId(role: IUserRole): string | undefined {
  const value = role.default_app_dashboard;
  return typeof value === 'string' ? value : value?._id;
}

@Injectable({ providedIn: 'root' })
export class DashboardSelectionService {
  constructor(
    private readonly modelService: ModelService,
    private readonly sessionService: SessionService,
    private readonly permissionLoader: PermissionLoader,
  ) {}

  /** Loads the current user's dashboards (fresh), grouped, plus the relevant permission flags. */
  async loadView(): Promise<IDashboardSelectionView | null> {
    const user = await this.loadUser();
    if (!user) {
      return null;
    }

    const [groups, permissions] = await Promise.all([
      this.resolveGroups(user),
      this.resolvePermissions(user),
    ]);

    return {
      groups,
      activeDashboardId: user.active_dashboard,
      blockedDashboardIds: this.resolveBlockedDashboardIds(user, groups),
      ...permissions,
    };
  }

  // Re-reads the user so a role change since the list was rendered cannot switch to a lost dashboard.
  async activate(dashboard: IAppDashboard): Promise<DashboardActivation> {
    const view = await this.loadView();
    if (!view) {
      throw new Error('User request failed.');
    }

    const listed = [...view.groups.standard, ...view.groups.personal]
      .some((candidate: IAppDashboard) => candidate._id === dashboard._id);
    if (!listed || view.blockedDashboardIds.includes(dashboard._id)) {
      console.warn(
        `[DashboardSelectionService] refused to activate "${dashboard.name}" (${dashboard._id}): ` +
          (listed ? 'personal copy missing' : 'no longer available for the user\'s roles'),
      );
      return 'denied';
    }

    await this.sessionService.user.changeActiveDashboard(dashboard._id);
    return 'activated';
  }

  /** Publishes a dashboard; returns the published copy without mutating the input. */
  async publish(dashboard: IAppDashboard): Promise<IAppDashboard> {
    const published: IAppDashboard = { ...dashboard, isPublished: true };
    await this.modelService._appDashboard.upsertOne(published);
    return published;
  }

  private async loadUser(): Promise<IUser | null> {
    const sessionUser = this.sessionService.user.getUser();
    if (!sessionUser) {
      return null;
    }
    return this.modelService._user.getById(sessionUser._id, 'roles,app_dashboards');
  }

  // Hides defaults of roles the user lacks; lists role defaults login has not assigned yet.
  private async resolveGroups(user: IUser): Promise<IDashboardGroups> {
    const enabledRoles: IUserRole[] = await this.modelService._userRole.listAll('enabled=true');
    const userDefaultIds = user.roles.map(defaultDashboardId).filter((id): id is string => !!id);
    const roleDefaultIds = new Set(enabledRoles.map(defaultDashboardId));

    const available = user.app_dashboards.filter(
      (dashboard: IAppDashboard) =>
        !dashboard.isPublished ||
        !roleDefaultIds.has(dashboard._id) ||
        (dashboard.roleAssignable && userDefaultIds.includes(dashboard._id)),
    );

    const assignedIds = new Set(user.app_dashboards.map((dashboard: IAppDashboard) => dashboard._id));
    const missingIds = [...new Set(userDefaultIds)].filter((id: string) => !assignedIds.has(id));
    const missing = (await Promise.all(
      missingIds.map((id: string) => this.modelService._appDashboard.getById(id)),
    )).filter((dashboard): dashboard is IAppDashboard => !!dashboard);

    const groups = splitDashboards(available);
    return { standard: [...groups.standard, ...missing], personal: groups.personal };
  }

  private resolveBlockedDashboardIds(user: IUser, groups: IDashboardGroups): string[] {
    const hasPersonalCopy = user.app_dashboards.some(
      (dashboard: IAppDashboard) =>
        dashboard.type === 'copy' && dashboard.isPublished == null && !dashboard.roleAssignable,
    );
    const hasStandardCopy = user.app_dashboards.some(
      (dashboard: IAppDashboard) =>
        dashboard.type === 'copy' && dashboard.isPublished === true && dashboard.roleAssignable === true,
    );
    if (hasPersonalCopy || !hasStandardCopy) {
      return [];
    }
    return groups.standard
      .filter((dashboard: IAppDashboard) => dashboard.type === 'copy')
      .map((dashboard: IAppDashboard) => dashboard._id);
  }

  private async resolvePermissions(user: IUser): Promise<{
    canCreateStandard: boolean;
    canListStandard: boolean;
    canListPersonal: boolean;
  }> {
    const flags = { canCreateStandard: false, canListStandard: false, canListPersonal: false };

    const activeDashboard =
      user.app_dashboards.find((d: IAppDashboard) => d._id === user.active_dashboard) ??
      user.app_dashboards[0];
    if (!activeDashboard) {
      return flags;
    }

    const permissions = await this.permissionLoader.load(
      activeDashboard,
      user,
    );
    for (const pluginPermissions of permissions) {
      const modulePermissions: IMenuItemModulePermission | undefined =
        pluginPermissions.menuitem_permissions?.['dashboard'];
      if (!modulePermissions) {
        continue;
      }
      flags.canCreateStandard =
        flags.canCreateStandard ||
        getPermissionValue(modulePermissions, 'core_plugin_dashboard_default_dashboard_create');
      flags.canListStandard =
        flags.canListStandard ||
        getPermissionValue(modulePermissions, 'core_plugin_dashboard_default_dashboard_list');
      flags.canListPersonal =
        flags.canListPersonal ||
        getPermissionValue(modulePermissions, 'core_plugin_dashboard_custom_dashboard_list');
    }

    return flags;
  }
}
